import { useMemo } from 'react';
import { TAX_TYPES, calculateTaxAmount, getInvoiceDeductionRate } from '../../constants/taxTypes';
import { ACCOUNT_TYPES } from '../../constants/accountTypes';
import { formatCurrency } from '../../utils/formatting';

export const TaxSummary = ({ accounts, journals, currentFiscalYear }) => {
    const taxData = useMemo(() => {
        let salesTax10 = 0;
        let salesTax8 = 0;
        let purchaseTax10 = 0;
        let purchaseTax8 = 0;
        let purchaseTax10NonInvoice = 0;
        let purchaseTax8NonInvoice = 0;

        journals.forEach(journal => {
            journal.lines.forEach(line => {
                // 課税仕入・課税売上を含む全ての課税取引を対象
                const isTaxable = line.taxType === TAX_TYPES.TAXABLE ||
                                  (typeof line.taxType === 'string' && line.taxType.includes('課税'));

                if (!isTaxable) return;

                const account = accounts.find(a => a.id === line.accountId);
                if (!account) return;

                const taxAmount = line.taxAmount || calculateTaxAmount(line.amount, line.taxRate || 10);
                const isRevenue = account.type === ACCOUNT_TYPES.REVENUE;
                const isExpense = account.type === ACCOUNT_TYPES.EXPENSE;
                const rate = line.taxRate || 10;

                if (isRevenue) {
                    if (rate === 10) salesTax10 += taxAmount;
                    else if (rate === 8) salesTax8 += taxAmount;
                } else if (isExpense) {
                    const deductionRate = getInvoiceDeductionRate(journal.date, line.isInvoiceQualified !== false);
                    const deductibleTax = Math.floor(taxAmount * deductionRate / 100);
                    const nonDeductibleTax = taxAmount - deductibleTax;

                    if (rate === 10) {
                        purchaseTax10 += deductibleTax;
                        purchaseTax10NonInvoice += nonDeductibleTax;
                    } else if (rate === 8) {
                        purchaseTax8 += deductibleTax;
                        purchaseTax8NonInvoice += nonDeductibleTax;
                    }
                }
            });
        });

        const totalSalesTax = salesTax10 + salesTax8;
        const totalPurchaseTax = purchaseTax10 + purchaseTax8;
        const taxPayable = totalSalesTax - totalPurchaseTax;

        return {
            salesTax10, salesTax8, totalSalesTax,
            purchaseTax10, purchaseTax8, totalPurchaseTax,
            purchaseTax10NonInvoice, purchaseTax8NonInvoice,
            taxPayable
        };
    }, [journals, accounts]);

    return (
        <div className="page-container">
            <h2>消費税集計</h2>
            <p style={{ color: '#666', marginBottom: '24px' }}>{currentFiscalYear}年度の消費税データ</p>

            <div className="card" style={{ marginBottom: '24px' }}>
                <h3>売上にかかる消費税（仮受消費税）</h3>
                <table className="data-table">
                    <tbody>
                        <tr>
                            <td>10%分</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(taxData.salesTax10)} 円</td>
                        </tr>
                        <tr>
                            <td>8%分（軽減税率）</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(taxData.salesTax8)} 円</td>
                        </tr>
                        <tr style={{ fontWeight: 'bold', borderTop: '2px solid #333' }}>
                            <td>合計</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(taxData.totalSalesTax)} 円</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="card" style={{ marginBottom: '24px' }}>
                <h3>仕入にかかる消費税（仮払消費税）</h3>
                <table className="data-table">
                    <tbody>
                        <tr>
                            <td>10%分（控除可能）</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(taxData.purchaseTax10)} 円</td>
                        </tr>
                        <tr>
                            <td>8%分（控除可能）</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(taxData.purchaseTax8)} 円</td>
                        </tr>
                        <tr style={{ fontWeight: 'bold', borderTop: '2px solid #333' }}>
                            <td>控除可能合計</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(taxData.totalPurchaseTax)} 円</td>
                        </tr>
                    </tbody>
                </table>
                {(taxData.purchaseTax10NonInvoice > 0 || taxData.purchaseTax8NonInvoice > 0) && (
                    <div style={{ marginTop: '16px', padding: '12px', background: '#fff3cd', borderRadius: '4px' }}>
                        <p style={{ margin: 0, fontSize: '14px' }}>
                            <strong>非インボイス分（控除不可）:</strong><br />
                            10%: {formatCurrency(taxData.purchaseTax10NonInvoice)} 円 /
                            8%: {formatCurrency(taxData.purchaseTax8NonInvoice)} 円
                        </p>
                    </div>
                )}
            </div>

            <div className="card" style={{ background: taxData.taxPayable >= 0 ? '#e8f5e9' : '#ffebee' }}>
                <h3>消費税納税予測額</h3>
                <p style={{ fontSize: '32px', fontWeight: 'bold', margin: '16px 0', color: taxData.taxPayable >= 0 ? '#2e7d32' : '#c62828' }}>
                    {formatCurrency(taxData.taxPayable)} 円
                </p>
                <p style={{ color: '#666', fontSize: '14px' }}>
                    {taxData.taxPayable >= 0 ? '納付予定' : '還付予定'}（仮受消費税 - 仮払消費税）
                </p>
            </div>

            <div style={{ marginTop: '24px', padding: '16px', background: '#f5f5f5', borderRadius: '4px' }}>
                <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                    ※ 仕訳入力時に税区分を「課税」に設定した取引のみ集計されます。<br />
                    ※ インボイス制度の経過措置（2026年9月まで80%、2029年9月まで50%控除）が適用されます。
                </p>
            </div>
        </div>
    );
};

export default TaxSummary;
